import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "dropdown", "selectedItems"];
  static values = {
    url: String,
    selected: String,
  };

  connect() {
    this.hideDropdown();
    this.selectedMediaItems = [];
    document.addEventListener("click", this.handleClickOutside.bind(this));
    this.loadExistingSelections();
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this));
  }

  search() {
    const query = this.inputTarget.value;

    if (query.length < 2) {
      this.hideDropdown();
      return;
    }

    this.showDropdown();

    fetch(`/admin/media/search?q=${encodeURIComponent(query)}`, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        this.renderResults(data);
      })
      .catch((error) => {
        console.error("Search error:", error);
        this.hideDropdown();
      });
  }

  renderResults(results) {
    if (!results || results.length === 0) {
      this.dropdownTarget.innerHTML =
        '<div class="p-2 text-gray-500">No results found</div>';
      return;
    }

    // Filter out already selected items
    const availableResults = results.filter(
      (item) =>
        !this.selectedMediaItems.some(
          (selected) => selected.id === item.id && selected.type === item.type
        )
    );

    if (availableResults.length === 0) {
      this.dropdownTarget.innerHTML =
        '<div class="p-2 text-gray-500">All matching items already selected</div>';
      return;
    }

    const html = availableResults
      .map(
        (item) => `
      <div class="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
           data-action="click->media-search#selectMedia"
           data-media-id="${item.id}"
           data-media-type="${item.type}"
           data-media-name="${item.title}">
        <div class="font-medium">${item.title}</div>
        <div class="text-sm text-gray-500">${item.type}</div>
      </div>
    `
      )
      .join("");

    this.dropdownTarget.innerHTML = html;
  }

  selectMedia(event) {
    event.preventDefault();
    const mediaId = event.currentTarget.dataset.mediaId;
    const mediaType = event.currentTarget.dataset.mediaType;
    const mediaName = event.currentTarget.dataset.mediaName;

    const mediaItem = {
      id: parseInt(mediaId),
      type: mediaType,
      name: mediaName,
    };

    this.selectedMediaItems.push(mediaItem);
    this.updateSelectedItemsDisplay();
    this.updateHiddenInputs();

    // Clear and hide search
    this.inputTarget.value = "";
    this.hideDropdown();
  }

  removeMedia(event) {
    event.preventDefault();
    const mediaId = parseInt(event.currentTarget.dataset.mediaId);
    const mediaType = event.currentTarget.dataset.mediaType;

    this.selectedMediaItems = this.selectedMediaItems.filter(
      (item) => !(item.id === mediaId && item.type === mediaType)
    );

    this.updateSelectedItemsDisplay();
    this.updateHiddenInputs();
  }

  updateSelectedItemsDisplay() {
    if (this.selectedMediaItems.length === 0) {
      this.selectedItemsTarget.innerHTML =
        '<div class="text-gray-500 text-sm">No media items selected</div>';
      return;
    }

    const html = this.selectedMediaItems
      .map(
        (item) => `
      <div class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 mr-2 mb-2">
        <span>${item.name}</span>
        <button type="button"
                class="ml-2 text-blue-600 hover:text-blue-800"
                data-action="click->media-search#removeMedia"
                data-media-id="${item.id}"
                data-media-type="${item.type}">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `
      )
      .join("");

    this.selectedItemsTarget.innerHTML = html;
  }

  updateHiddenInputs() {
    // Remove existing hidden inputs
    const existingInputs = this.element.querySelectorAll(
      'input[name="article[media_item_ids][]"]'
    );
    existingInputs.forEach((input) => input.remove());

    // Add new hidden inputs for each selected item
    this.selectedMediaItems.forEach((item) => {
      const hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = "article[media_item_ids][]";
      hiddenInput.value = JSON.stringify({
        media_id: item.id,
        media_type: item.type,
      });
      this.element.appendChild(hiddenInput);
    });
  }

  loadExistingSelections() {
    // Load any existing selections from the selectedValue
    try {
      if (this.selectedValue && this.selectedValue.length > 0) {
        // Decode HTML entities and parse JSON
        const decodedValue = this.selectedValue
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">");

        const parsed = JSON.parse(decodedValue);

        if (Array.isArray(parsed) && parsed.length > 0) {
          this.selectedMediaItems = [...parsed];
          this.updateSelectedItemsDisplay();
          this.updateHiddenInputs();
        }
      }
    } catch (error) {
      console.error("Error loading existing selections:", error);
      this.selectedMediaItems = [];
      this.updateSelectedItemsDisplay();
    }
  }

  showDropdown() {
    this.dropdownTarget.classList.remove("hidden");
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden");
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown();
    }
  }
}
